import { useState, useEffect, useRef, useCallback, forwardRef } from "react";

const SlideDown = forwardRef(function SlideDown({ animate, onDone, children }, ref) {
  const innerRef = useRef(null);
  const [height, setHeight] = useState(animate ? 0 : "auto");
  const [opacity, setOpacity] = useState(animate ? 0 : 1);

  useEffect(() => {
    if (!animate) return;
    const el = innerRef.current;
    if (!el) return;
    // Measure natural height
    const h = el.scrollHeight;
    // Start at 0, then animate to full height
    requestAnimationFrame(() => {
      setHeight(h);
      setOpacity(1);
    });
    const timer = setTimeout(() => {
      setHeight("auto");
      onDone?.();
    }, 350);
    return () => clearTimeout(timer);
  }, [animate, onDone]);

  return (
    <div
      ref={ref}
      style={{
        height,
        opacity,
        overflow: height === "auto" ? "visible" : "hidden",
        transition: "height 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease",
      }}
    >
      <div ref={innerRef} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {children}
      </div>
    </div>
  );
});

let googleMapsLoaded = false;
let googleMapsLoading = false;
const loadCallbacks = [];

function ensureGoogleMaps(apiKey) {
  if (googleMapsLoaded) return Promise.resolve();
  if (googleMapsLoading) {
    return new Promise(resolve => loadCallbacks.push(resolve));
  }
  googleMapsLoading = true;
  return new Promise((resolve, reject) => {
    loadCallbacks.push(resolve);
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => {
      googleMapsLoaded = true;
      loadCallbacks.forEach(cb => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => reject(new Error("Google Maps failed to load"));
    document.head.appendChild(script);
  });
}

/**
 * AddressInput — single autocomplete field that expands to structured fields on selection.
 *
 * Props:
 *   values: { street, street2, city, state, zip }
 *   onChange(updates): called with partial { street?, street2?, city?, state?, zip? }
 *   expanded: optional boolean override (e.g. true when editing existing address)
 */
export default function AddressInput({ values = {}, onChange, expanded: expandedProp, initialLocked = false, manualMode = false }) {
  const { street = "", street2 = "", city = "", state = "", zip = "" } = values;
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [locked, setLocked] = useState(initialLocked);
  const expanded = manualMode || (expandedProp != null ? expandedProp : internalExpanded);
  const expandedFieldsRef = useRef(null);
  const inputRef = useRef(null);
  const acRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

  // Auto-expand if values are populated (e.g. editing or order pre-fill)
  useEffect(() => {
    if (street || city || state || zip) {
      setInternalExpanded(true);
      setAnimating(false);
    }
  }, [street, city, state, zip]);

  // Sync locked state when initialLocked changes (e.g. order address loaded)
  useEffect(() => {
    setLocked(initialLocked);
  }, [initialLocked]);

  // Detach autocomplete when manual mode is toggled on
  useEffect(() => {
    if (manualMode && acRef.current) {
      window.google?.maps?.event?.clearInstanceListeners(acRef.current);
      acRef.current = null;
    }
  }, [manualMode]);

  const expandWithAnimation = useCallback(() => {
    setAnimating(true);
    setInternalExpanded(true);
  }, []);

  useEffect(() => {
    if (manualMode || !apiKey || acRef.current || !inputRef.current) return;

    ensureGoogleMaps(apiKey).then(() => {
      if (!inputRef.current || acRef.current) return;

      const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "us" },
        fields: ["address_components"],
        types: ["address"],
      });

      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place.address_components) return;

        const find = (type) => place.address_components.find(c => c.types.includes(type));
        const parsedStreet = [find("street_number")?.long_name, find("route")?.long_name].filter(Boolean).join(" ");
        const parsedCity = find("locality")?.long_name || find("sublocality_level_1")?.long_name || "";
        const parsedState = find("administrative_area_level_1")?.short_name || "";
        const parsedZip = find("postal_code")?.short_name || "";

        if (inputRef.current) inputRef.current.value = parsedStreet;
        expandWithAnimation();
        setLocked(true);
        onChangeRef.current({ street: parsedStreet, city: parsedCity, state: parsedState, zip: parsedZip });
      });

      acRef.current = ac;
    }).catch(() => {
      // Falls back to plain text input — manual entry still works
    });
  }, [apiKey, expandWithAnimation, manualMode]);

  const handleChangeAddress = useCallback(() => {
    setLocked(false);
    setInternalExpanded(false);
    setAnimating(false);
    onChangeRef.current({ street: "", street2: "", city: "", state: "", zip: "" });
    if (acRef.current) {
      acRef.current = null;
    }
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = "";
        inputRef.current.focus();
      }
      // Re-init autocomplete
      if (apiKey && inputRef.current) {
        ensureGoogleMaps(apiKey).then(() => {
          if (!inputRef.current) return;
          const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
            componentRestrictions: { country: "us" },
            fields: ["address_components"],
            types: ["address"],
          });
          ac.addListener("place_changed", () => {
            const place = ac.getPlace();
            if (!place.address_components) return;
            const find = (type) => place.address_components.find(c => c.types.includes(type));
            const parsedStreet = [find("street_number")?.long_name, find("route")?.long_name].filter(Boolean).join(" ");
            const parsedCity = find("locality")?.long_name || find("sublocality_level_1")?.long_name || "";
            const parsedState = find("administrative_area_level_1")?.short_name || "";
            const parsedZip = find("postal_code")?.short_name || "";
            if (inputRef.current) inputRef.current.value = parsedStreet;
            expandWithAnimation();
            setLocked(true);
            onChangeRef.current({ street: parsedStreet, city: parsedCity, state: parsedState, zip: parsedZip });
          });
          acRef.current = ac;
        });
      }
    }, 50);
  }, [apiKey, expandWithAnimation]);

  const update = useCallback((field, val) => {
    onChangeRef.current({ [field]: val });
  }, []);

  if (!expanded && !manualMode) {
    return (
      <div>
        <input
          ref={inputRef}
          defaultValue={street}
          placeholder="Start typing your address..."
          autoComplete="street-address"
          onChange={() => {}}
        />
        <p style={{ fontSize: "11px", color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 4, letterSpacing: "0.03em" }}>
          Select an address from the dropdown to continue
        </p>
      </div>
    );
  }

  const lockedStyle = locked ? { background: "var(--surface2)", color: "var(--text-dim)", cursor: "not-allowed", opacity: 0.8 } : {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <input
        ref={manualMode ? undefined : inputRef}
        {...(manualMode ? { value: street, onChange: e => update("street", e.target.value) } : { defaultValue: street, onChange: () => {} })}
        placeholder="Address line 1"
        autoComplete="address-line1"
        readOnly={!manualMode && locked}
        onClick={!manualMode && locked ? handleChangeAddress : undefined}
        style={manualMode ? {} : { ...lockedStyle, ...(locked ? { cursor: "pointer" } : {}) }}
      />
      <SlideDown animate={animating} onDone={() => setAnimating(false)} ref={expandedFieldsRef}>
        <input
          value={street2}
          onChange={e => update("street2", e.target.value)}
          placeholder="Apt, suite, unit (optional)"
          autoComplete="address-line2"
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 10 }}>
          <input
            value={city}
            placeholder="City"
            readOnly={!manualMode}
            tabIndex={manualMode ? undefined : -1}
            onFocus={manualMode ? undefined : (e => e.target.blur())}
            onChange={manualMode ? (e => update("city", e.target.value)) : undefined}
            style={manualMode ? {} : { background: "var(--surface2)", color: "var(--text-dim)", cursor: "default", pointerEvents: "none" }}
          />
          <input
            value={state}
            placeholder="State"
            readOnly={!manualMode}
            tabIndex={manualMode ? undefined : -1}
            onFocus={manualMode ? undefined : (e => e.target.blur())}
            onChange={manualMode ? (e => update("state", e.target.value)) : undefined}
            style={manualMode ? {} : { background: "var(--surface2)", color: "var(--text-dim)", cursor: "default", pointerEvents: "none" }}
          />
          <input
            value={zip}
            placeholder="ZIP"
            readOnly={!manualMode}
            tabIndex={manualMode ? undefined : -1}
            onFocus={manualMode ? undefined : (e => e.target.blur())}
            onChange={manualMode ? (e => update("zip", e.target.value)) : undefined}
            style={manualMode ? {} : { background: "var(--surface2)", color: "var(--text-dim)", cursor: "default", pointerEvents: "none" }}
          />
        </div>
        {locked && (
          <button
            type="button"
            onClick={handleChangeAddress}
            style={{
              background: "none", border: "none", padding: "4px 0",
              fontSize: "12px", color: "var(--amber)", cursor: "pointer",
              fontFamily: "var(--font-mono)", letterSpacing: "0.03em", textAlign: "left",
            }}
          >
            Change address
          </button>
        )}
      </SlideDown>
    </div>
  );
}
